import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { formatError } from '../lib/errors';
import { formatVenueDateTime } from '../lib/time';
import { bookingDisplayName } from '../lib/guest-name';
import type { Departure } from '../lib/departures';
import type { BookingRow, LifecycleStatus } from '../types';
import { LifecyclePill, PaymentPill } from './StatusPill';
import { CheckinButton } from './CheckinButton';
import { useSingleRoomLocations } from '../lib/room-counts';

interface Props {
  // All departures sharing the same (date, time). The modal lists every
  // booking in that slot regardless of which experience it is — operators
  // care about the slot's occupants, not the experience subdivision.
  departures: Departure[];
  onClose: () => void;
}

// Reads through the new departure layer. The query targets every
// departure with at least one slot at this (slot_start, room_in) —
// !inner on booking_slots narrows the join + drops parents without a
// matching slot. Slots are filtered post-fetch to just the matching
// (slot_start, room) so we don't pull the rest of the departure's
// split-slot legs into the modal.
// Both `departure_experiences` AND `booking_slots` need `!inner` so the
// slot filter (.eq slot_start, .in room_id) actually narrows the
// departures returned. With only `booking_slots!inner`, a departure
// whose picks have no matching slots still comes through with
// `departure_experiences: []` — the modal then renders an empty 0p row
// for every unrelated departure in the booking schema. The double-inner
// drops those entirely so we only see departures actually at this slot.
const SELECT = `
  id, departure_number, lifecycle_status, payment_status,
  locale, notes, created_at,
  booking:bookings!inner (
    id, customer_id, guest_name,
    customers ( first_name, last_name, email, phone )
  ),
  departure_experiences!inner (
    id, experience_id, party_size, price_cents, pick_index,
    booking_slots!inner ( id, slot_start, party_size, room_id, slot_index )
  )
`;

interface RawSlot { id: string; slot_start: string; party_size: number; room_id: string; slot_index: number }
interface RawPick {
  id: string;
  experience_id: string;
  party_size: number;
  price_cents: number | null;
  pick_index: number;
  booking_slots: RawSlot[];
}
interface RawDeparture {
  id: string;
  departure_number: string;
  lifecycle_status: LifecycleStatus;
  payment_status: BookingRow['payment_status'];
  locale: string;
  notes: string | null;
  created_at: string;
  booking: {
    id: string;
    customer_id: string | null;
    guest_name: string | null;
    customers: BookingRow['customers'];
  };
  departure_experiences: RawPick[];
}

export function DepartureModal({ departures, onClose }: Props) {
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const singleRoomLocations = useSingleRoomLocations();

  const first = departures[0]!;
  const showRoom = !singleRoomLocations.has(first.location_id);

  // Experience lookup so we can label each booking row when the slot
  // spans more than one experience.
  const expById = new Map<string, { name: string }>();
  for (const d of departures) {
    if (!expById.has(d.experience_id)) {
      expById.set(d.experience_id, { name: d.experience_name });
    }
  }
  const multiExperience = expById.size > 1;

  // Header total: every row's `capacity` and `booked` are already room-wide,
  // so dedupe by room_id and sum across rooms.
  const byRoom = new Map<string, { capacity: number; booked: number }>();
  for (const d of departures) {
    if (d.capacity > 0) byRoom.set(d.room_id, { capacity: d.capacity, booked: d.booked });
  }
  const totalCapacity = [...byRoom.values()].reduce((n, r) => n + r.capacity, 0);
  const totalBooked   = [...byRoom.values()].reduce((n, r) => n + r.booked,   0);

  // All departures in a slot share the same slot_start (computed from
  // date + time_of_day at venue tz). Rooms can differ — query by IN(...).
  const roomIds = [...new Set(departures.map((d) => d.room_id))];
  const slotStart = first.slot_start;
  // Room name lookup for hydrating the BookingSlot stub.
  const roomNameById = new Map<string, string>();
  for (const d of departures) {
    if (!roomNameById.has(d.room_id)) roomNameById.set(d.room_id, d.room_name);
  }
  const roomIdsKey = roomIds.join(',');

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const { data, error: qErr } = await supabase
        .schema('booking')
        .from('departures')
        .select(SELECT)
        .in('departure_experiences.booking_slots.room_id', roomIds)
        .eq('departure_experiences.booking_slots.slot_start', slotStart)
        .neq('lifecycle_status', 'cancelled')
        .order('created_at', { ascending: true });

      if (qErr) throw qErr;

      const rows = (data ?? []) as unknown as RawDeparture[];
      const hydrated: BookingRow[] = rows.map((r) => {
        const picks = [...r.departure_experiences]
          .sort((a, b) => a.pick_index - b.pick_index)
          .map((pick) => ({
            id: pick.id,
            experience_id: pick.experience_id,
            party_size: pick.party_size,
            price_cents: pick.price_cents,
            pick_index: pick.pick_index,
            experiences: {
              id: pick.experience_id,
              name: expById.get(pick.experience_id)?.name ?? '',
              booking_image_url: null,
            },
          }));
        // Only the slots matching THIS slot_start in THESE rooms — drop
        // any other split-slot legs that belong to the same departure.
        const matchingSlots = r.departure_experiences.flatMap((pick) =>
          pick.booking_slots
            .filter(
              (s) => s.slot_start === slotStart && roomIds.includes(s.room_id),
            )
            .map((s) => ({
              id: s.id,
              slot_start: s.slot_start,
              party_size: s.party_size,
              room_id: s.room_id,
              slot_index: s.slot_index,
              rooms: { name: roomNameById.get(s.room_id) ?? '' },
              departure_experience_id: pick.id,
            })),
        );
        const partySize = picks.reduce((sum, p) => sum + p.party_size, 0);
        const totalCents = picks.reduce(
          (sum, p) => sum + (p.price_cents ?? 0),
          0,
        );
        return {
          id: r.id,
          booking_number: r.departure_number,
          lifecycle_status: r.lifecycle_status,
          payment_status: r.payment_status,
          party_size: partySize,
          locale: r.locale,
          notes: r.notes,
          total_cents: totalCents > 0 ? totalCents : null,
          created_at: r.created_at,
          parent_booking_id: r.booking.id,
          guest_name: r.booking.guest_name ?? null,
          customers: r.booking.customers,
          booking_slots: matchingSlots,
          experiences: picks[0]?.experiences ?? null,
          experience_picks: picks,
        };
      });

      setBookings(hydrated);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomIdsKey, slotStart]);

  useEffect(() => {
    void load();
  }, [load]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onLifecycleChange = (id: string, next: LifecycleStatus) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, lifecycle_status: next } : b)),
    );
  };

  // Header title: when a slot runs a single experience, use its name; when
  // multiple, just say "Vertrek" so the title isn't misleading.
  const headerTitle = multiExperience
    ? 'Vertrek'
    : first.experience_name;
  // Room subtitle: for single-room locations the name is noise; for
  // multi-room slots that share the same room, show that room; for
  // slots that span multiple rooms, show none (room appears per row).
  const headerRoom =
    !showRoom || roomIds.length > 1 ? null : roomNameById.get(roomIds[0]!) ?? null;

  return (
    <div className="dep-modal-backdrop" onClick={onClose}>
      <div className="dep-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div>
            <h2>{headerTitle}</h2>
            <div className="dep-modal-sub muted">
              {formatVenueDateTime(slotStart)}
              {headerRoom ? <> · {headerRoom}</> : null}
              {' · '}
              {totalBooked} / {totalCapacity}
            </div>
          </div>
          <button className="btn-link dep-modal-close" onClick={onClose} aria-label="Sluiten">
            ✕
          </button>
        </header>

        <div className="dep-modal-body">
          {loading ? <div className="muted">Laden…</div> : null}
          {error ? <div className="error-card">{error}</div> : null}
          {!loading && !error && bookings.length === 0 ? (
            <div className="muted">Geen boekingen voor deze sessie.</div>
          ) : null}

          <ul className="dep-booking-list">
            {bookings.map((b) => {
              const c = b.customers;
              const fullName = bookingDisplayName(b) || '—';
              // CheckinButton already renders "✓ Aangekomen" on arrived
              // bookings; the lifecycle pill would just duplicate that label.
              // Same for cancelled (filtered out above, but defensive).
              const showLifecyclePill =
                b.lifecycle_status !== 'arrived' &&
                b.lifecycle_status !== 'cancelled';
              return (
                <li key={b.id} className="dep-booking-row">
                  <div className="dep-booking-main">
                    <Link to={`/booking/${b.booking_number}`} className="dep-booking-link">
                      <strong className="dep-booking-name">{fullName}</strong>
                      <span className="dep-booking-party muted"> · {b.party_size}p</span>
                      {multiExperience ? (
                        <span className="dep-booking-exp"> · {b.experiences?.name ?? '—'}</span>
                      ) : null}
                    </Link>
                    <div className="dep-booking-sub muted">
                      <span className="dep-booking-number">{b.booking_number}</span>
                      {c?.email ? <> · {c.email}</> : null}
                      {c?.phone ? <> · {c.phone}</> : null}
                    </div>
                  </div>
                  <div className="dep-booking-pills">
                    {showLifecyclePill ? (
                      <LifecyclePill status={b.lifecycle_status} />
                    ) : null}
                    <PaymentPill status={b.payment_status} />
                  </div>
                  <div className="dep-booking-action">
                    <CheckinButton
                      booking={b}
                      onChanged={(next) => onLifecycleChange(b.id, next)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
